import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { FormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { FormNumberInput } from '@src/orders/PermitFormSections/FormNumberInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  IsUsedMobileHomeField,
  LoadIsMobileHomeField,
  MobileHomeBoxLengthField,
  MobileHomeBoxWidthField,
  MobileHomeCurbsideEavesLengthField,
  MobileHomeEaveWidthField,
  mobileHomeFields,
  MobileHomeHitchLengthField,
  MobileHomeHudNumberField,
  MobileHomeMakeField,
  MobileHomeRoadsideEavesLengthField,
  MobileHomeSerialNumberField,
  MobileHomeYearField,
} from '@src/permitField/MobileHomeFields';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const MobileHomeSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const {
    register,
    control,
    formState: { errors, disabled },
    getValues,
    unregister,
  } = useFormContext<FormEntries<typeof props.fields>>();

  const [loadIsMobileHome] = useWatch({
    control,
    name: [LoadIsMobileHomeField.fieldName],
  });

  const onUncheckIsMobileHome = () => {
    Object.keys(mobileHomeFields).forEach((mobileHomeField) => {
      if (mobileHomeField === LoadIsMobileHomeField.fieldName) return;

      unregister(mobileHomeField);
    });
  };

  const onIsMobileHomeChanged = () => {
    const isChecked = getValues(LoadIsMobileHomeField.fieldName);
    if (isChecked) return;
    onUncheckIsMobileHome();
  };

  const { sectionInError } = useSectionError(Object.keys(mobileHomeFields));

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <Stack gap={theme.spacing['3']}>
      <ConditionalField
        fields={props.fields}
        conditions={LoadIsMobileHomeField.fieldName}
      >
        <FormCheckbox
          disabled={
            disabled || isFieldDisabled(LoadIsMobileHomeField.fieldName)
          }
          register={register}
          errors={errors}
          fieldName={LoadIsMobileHomeField.fieldName}
          defaultChecked={getValues(LoadIsMobileHomeField.fieldName) as boolean}
          onChange={onIsMobileHomeChanged}
        />
      </ConditionalField>

      {loadIsMobileHome && (
        <EditOrderCollapsible
          title={t('mobileHome')}
          id="mobile-home"
          collapseEventName={props.collapseEventName}
          uncollapseEventName={props.uncollapseEventName}
          inError={sectionInError}
        >
          <Stack gap={theme.spacing['3']}>
            <ConditionalField
              fields={props.fields}
              conditions={IsUsedMobileHomeField.fieldName}
            >
              <FormCheckbox
                disabled={
                  disabled || isFieldDisabled(IsUsedMobileHomeField.fieldName)
                }
                register={register}
                errors={errors}
                fieldName={IsUsedMobileHomeField.fieldName}
                defaultChecked={
                  getValues(IsUsedMobileHomeField.fieldName) as boolean
                }
              />
            </ConditionalField>

            <Stack direction="row" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeYearField.fieldName}
              >
                <FormNumberInput
                  register={register}
                  errors={errors}
                  disabled={
                    disabled || isFieldDisabled(MobileHomeYearField.fieldName)
                  }
                  fieldName={MobileHomeYearField.fieldName}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeMakeField.fieldName}
              >
                <FormTextInput
                  register={register}
                  errors={errors}
                  disabled={
                    disabled || isFieldDisabled(MobileHomeMakeField.fieldName)
                  }
                  fieldName={MobileHomeMakeField.fieldName}
                />
              </ConditionalField>
            </Stack>

            <ConditionalField
              fields={props.fields}
              conditions={MobileHomeSerialNumberField.fieldName}
            >
              <FormTextInput
                register={register}
                errors={errors}
                disabled={
                  disabled ||
                  isFieldDisabled(MobileHomeSerialNumberField.fieldName)
                }
                fieldName={MobileHomeSerialNumberField.fieldName}
              />
            </ConditionalField>

            <Stack direction="row" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeHitchLengthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeHitchLengthField.fieldName}
                  disabled={isFieldDisabled(
                    MobileHomeHitchLengthField.fieldName,
                  )}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeEaveWidthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeEaveWidthField.fieldName}
                  disabled={isFieldDisabled(MobileHomeEaveWidthField.fieldName)}
                />
              </ConditionalField>
            </Stack>

            <Stack direction="row" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeBoxWidthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeBoxWidthField.fieldName}
                  disabled={isFieldDisabled(MobileHomeBoxWidthField.fieldName)}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeBoxLengthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeBoxLengthField.fieldName}
                  disabled={isFieldDisabled(MobileHomeBoxLengthField.fieldName)}
                />
              </ConditionalField>
            </Stack>

            <Stack direction="row" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeRoadsideEavesLengthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeRoadsideEavesLengthField.fieldName}
                  disabled={isFieldDisabled(
                    MobileHomeRoadsideEavesLengthField.fieldName,
                  )}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={MobileHomeCurbsideEavesLengthField.fieldName}
              >
                <FormDimensionInput
                  control={control}
                  fieldName={MobileHomeCurbsideEavesLengthField.fieldName}
                  disabled={isFieldDisabled(
                    MobileHomeCurbsideEavesLengthField.fieldName,
                  )}
                />
              </ConditionalField>
            </Stack>

            <ConditionalField
              fields={props.fields}
              conditions={MobileHomeHudNumberField.fieldName}
            >
              <FormTextInput
                register={register}
                errors={errors}
                disabled={
                  disabled ||
                  isFieldDisabled(MobileHomeHudNumberField.fieldName)
                }
                fieldName={MobileHomeHudNumberField.fieldName}
              />
            </ConditionalField>
          </Stack>
        </EditOrderCollapsible>
      )}
    </Stack>
  );
};
